import { useState, useRef } from "react";
import { ChevronDown, ChevronUp, Computer, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { InfringementSeverityBadge, TYPE_CONFIG } from "./badges";
import { InfringementActions } from "./InfringementActions";
import type { InfringementSourceAntiPiracy, InfringementSourceCdnAnalytics } from "@/types/domain";
import { useEdrsQuery, useInfringementActionMutation, useInfringementsQuery } from "@/lib/queries";
import { fmtDateTime } from "@/lib/utils";
import { Tooltip } from "@/components/ui/tooltip";

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start py-1.5 border-b border-border/20 last:border-0 gap-4">
      <span className="w-28 shrink-0 text-xs text-muted pt-px">{label}</span>
      <span className="flex-1 min-w-0 text-xs text-secondary">{children}</span>
    </div>
  );
}

function Section({
  title,
  children,
  defaultOpen = true,
  className,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={cn("border-b-2 border-border last:border-b-0", className)}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 w-full px-4 py-2.5 bg-surface-elevated cursor-pointer"
      >
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 text-muted/50 shrink-0 transition-transform duration-150",
            !open && "-rotate-90",
          )}
        />
        <span className="flex-1 text-left text-xs font-semibold text-muted/70">{title}</span>
      </button>
      {open && <div className="px-4 pt-3 pb-5">{children}</div>}
    </div>
  );
}

function Tags({ items, limit }: { items: string[]; limit?: number }) {
  const [expanded, setExpanded] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  if (items.length === 0) return <span className="text-xs text-muted/40">—</span>;

  const collapsed = limit !== undefined && !expanded && items.length > limit;
  const visible = collapsed ? items.slice(0, limit) : items;
  const hidden = items.length - visible.length;

  function handleToggle() {
    const next = !expanded;
    setExpanded(next);
    if (next) {
      // After expanding, scroll the toggle button into view so "less" is visible
      requestAnimationFrame(() => {
        toggleRef.current?.scrollIntoView({ behavior: "instant", block: "nearest" });
      });
    }
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {visible.map((v, i) => (
        <span
          key={i}
          className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-mono bg-surface-elevated border border-border/50 text-secondary"
        >
          {v}
        </span>
      ))}
      {limit !== undefined && items.length > limit && (
        <button
          ref={toggleRef}
          onClick={handleToggle}
          className="inline-flex items-center gap-0.5 px-2 py-1 rounded-md text-xs text-muted/60 hover:text-secondary border border-dashed border-border/40 hover:border-border/70 transition-colors cursor-pointer"
        >
          {expanded ? (
            <>
              <ChevronUp className="h-3 w-3" /> less
            </>
          ) : (
            <>
              <ChevronDown className="h-3 w-3" /> {hidden} more
            </>
          )}
        </button>
      )}
    </div>
  );
}

// Accepts an id rather than an Infringement object so the component always
// reads the live record from the React Query cache.
export function InfringementDetail({ infringementId }: { infringementId: string }) {
  const actionMutation = useInfringementActionMutation();
  const { data: edrs } = useEdrsQuery();
  const { data: infringementsData } = useInfringementsQuery();

  const infringement = infringementsData?.infringements.find((i) => i.id === infringementId);
  if (!infringement) return null;

  const edr = edrs?.find((e) => e.id === infringement.edr_id);
  const ap = infringement.details as InfringementSourceAntiPiracy;
  const isAntiPiracy =
    edr?.label === "Anti-Piracy" ||
    !!(ap as InfringementSourceAntiPiracy).ip ||
    !!(ap as InfringementSourceAntiPiracy).fqdn;
  const cdn = infringement.details as InfringementSourceCdnAnalytics;

  const ips = isAntiPiracy ? (ap.ip ? [String(ap.ip)] : []) : (cdn.ips ?? []);
  const fqdns = isAntiPiracy ? (ap.fqdn ? [ap.fqdn] : []) : [];
  const cdns = isAntiPiracy ? [] : (cdn.cdns ?? []);
  const tokens = cdn.tokens ?? [];
  const accounts = cdn.accounts ?? [];
  const channels = isAntiPiracy ? (ap.channel ? [ap.channel] : []) : (cdn.channels ?? []);

  const hasDetails =
    ips.length > 0 ||
    fqdns.length > 0 ||
    cdns.length > 0 ||
    (!isAntiPiracy && tokens.length > 0) ||
    accounts.length > 0 ||
    channels.length > 0;

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto min-h-0">
        {/* Overview */}
        <Section title="Overview">
          <Row label="ID">
            <span className="font-mono">{infringement.external_id}</span>
          </Row>
          <Row label="Tenant">{infringement.tenant_id}</Row>
          <Row label="Type">
            {TYPE_CONFIG[infringement.infringement_type as keyof typeof TYPE_CONFIG]?.label ??
              infringement.infringement_type.replace(/_/g, " ")}
          </Row>
          {edr && <Row label="Source">{edr.label}</Row>}
          {infringement.severity && (
            <Row label="Severity">
              <InfringementSeverityBadge severity={infringement.severity} />
            </Row>
          )}
          <Row label="First seen">{fmtDateTime(infringement.detected)}</Row>
          {infringement.lastseen && (
            <Row label="Last seen">{fmtDateTime(infringement.lastseen)}</Row>
          )}
          {infringement.event && <Row label="Event">{infringement.event}</Row>}
          {isAntiPiracy && ap.pirate_service && (
            <Row label="Pirate service">{ap.pirate_service}</Row>
          )}
        </Section>

        {/* Actions */}
        <Section title="Actions">
          <InfringementActions
            infringement={infringement}
            isBusy={actionMutation.isPending}
            onAction={(action) => {
              actionMutation.mutate({ id: infringement.id, action });
            }}
          />
        </Section>

        {/* Details */}
        {hasDetails && (
          <Section title="Details">
            {ips.length > 0 && (
              <Row label={`IPs (${ips.length})`}>
                <Tags items={ips} limit={3} />
              </Row>
            )}
            {fqdns.length > 0 && (
              <Row label={`FQDNs (${fqdns.length})`}>
                <Tags items={fqdns} limit={3} />
              </Row>
            )}
            {cdns.length > 0 && (
              <Row label={`CDNs (${cdns.length})`}>
                <Tags items={cdns} limit={3} />
              </Row>
            )}
            {!isAntiPiracy && tokens.length > 0 && (
              <Row label={`Tokens (${tokens.length})`}>
                <Tags items={tokens} limit={3} />
              </Row>
            )}
            {accounts.length > 0 && (
              <Row label={`Accounts (${accounts.length})`}>
                <Tags items={accounts} limit={3} />
              </Row>
            )}
            {channels.length > 0 && (
              <Row label={`Channels (${channels.length})`}>
                <Tags items={channels} limit={3} />
              </Row>
            )}
          </Section>
        )}

        {/* History */}
        {infringement.action_history?.length > 0 && (
          <Section title={`History (${infringement.action_history.length})`}>
            <div className="flex flex-col">
              {[...infringement.action_history]
                .sort((a, b) => b.timestamp.localeCompare(a.timestamp))
                .map((entry, i) => {
                  const result = entry.action_result ?? "";
                  const label = result
                    ? `${entry.action} — ${result.replace(/_/g, " ")}`
                    : entry.action;
                  return (
                    <div key={i} className="flex gap-3 group">
                      <div className="flex flex-col items-center pt-1">
                        <span className="h-1.5 w-1.5 rounded-full shrink-0 bg-border" />
                        <span className="flex-1 w-px bg-border/30 mt-1.5 group-last:hidden" />
                      </div>
                      <div className="pb-4 group-last:pb-0">
                        <span className="block text-xs text-secondary">
                          {label}
                          <span className="ml-1">
                            {entry.triggered_by == "autoblock" && (
                              <Tooltip content="Auto-blocked by system">
                                <Computer className="inline h-3.5 w-3.5" />
                              </Tooltip>
                            )}
                            {entry.triggered_by && entry.triggered_by != "autoblock" && (
                              <Tooltip content={`Triggered by ${entry.triggered_by}`}>
                                <User className="inline h-3.5 w-3.5" />
                              </Tooltip>
                            )}
                          </span>
                        </span>
                        <span className="block text-[11px] font-mono text-muted/60 mt-0.5">
                          {fmtDateTime(entry.timestamp)}
                        </span>
                      </div>
                    </div>
                  );
                })}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}
